import { memo, useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";
import { Whisper, Popover, WhisperInstance } from "rsuite";
import _ from "underscore";
import classNames from "classnames";
import { useAtomValue } from "jotai";

import { IsMobileAtom } from "../../App";
import { recordViewAtom } from "../../atoms/GlobalAtoms";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import AppTooltip from "../../elements/AppTooltip";
import FilterDropdownMenu from "../CRM/FilterDropdownMenu";
import { Module } from "../../types/TenantInterfaces";

interface Props {
  currentModule: Module | undefined | null;
  savedQuery?: any;
  query: any;
  filters?: any;
  setQuery: any;
  onApply?: (newQuery: any) => void;
  onClear?: () => void;
  onSave?: (newFilters: any, type: string) => void;
  checkForFields?: boolean;
  customComponent?: ({ onClick }: { onClick: () => void }) => JSX.Element;
  subitemFieldsOnly?: boolean;
  clearFilter?: () => void;
  getPageFilterName?: (filter: any) => string;
  showVariables?: boolean;
  saveNewFilter?: () => void;
  setManageDrawerOpen?: (open: boolean) => void;
  handleUpdateFilters?: () => void;
  disabled?: boolean;
  disableSave?: boolean;
  disableAdvancedFilter?: boolean;
}

const FiltersButton = ({
  currentModule,
  savedQuery,
  query,
  filters,
  setQuery,
  onApply,
  onClear,
  onSave,
  checkForFields = true,
  customComponent,
  subitemFieldsOnly = false,
  clearFilter,
  getPageFilterName,
  showVariables = false,
  saveNewFilter,
  setManageDrawerOpen,
  handleUpdateFilters,
  disabled,
  disableSave,
  disableAdvancedFilter,
}: Props) => {
  const recordViewOpen = useAtomValue(recordViewAtom);
  const mobileScreen = useAtomValue(IsMobileAtom);
  const [filterMode, setFilterMode] = useState<string | boolean>(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const filterDropdownRef = useRef<WhisperInstance>(null);

  const { cPermissions } = useCloudPermissions();

  const handleClear = () => {
    filterDropdownRef?.current?.close();

    callFunctionIfExists(clearFilter);
    callFunctionIfExists(onClear);
  };

  // Supports custom title for SavedFilters - Provides tooltip with custom title
  const handleButtonTitle = () => {
    if (!query?.Filters?.length || mobileScreen) return "Filter";

    if (query?.Metadata?.PageFilter && getPageFilterName) {
      const name = getPageFilterName(query?.Metadata?.PageFilter);

      if (name) {
        return (
          <AppTooltip
            content={name}
            delayOpen={800}
            placement="left"
            useTooltip
            useSpan
          >
            {name}
          </AppTooltip>
        );
      }
    }

    if (JSON.stringify(savedQuery) === JSON.stringify(query)) {
      return "Default Filter";
    }

    return "Custom Filter";
  };

  useEffect(() => {
    if (!query?.Metadata?.Type) return;

    setFilterMode(query?.Metadata?.Type ?? "quick");
  }, [query?.Metadata?.Type]);

  return (
    <Whisper
      placement={mobileScreen ? "bottom" : "autoVertical"}
      // TODO: Readd logic using (!keepOpen && !recordViewOpen) for advanced mode.
      trigger={
        disabled
          ? "none"
          : filterMode === "quick" || filterMode === "saved"
          ? "click"
          : "none"
      }
      ref={filterDropdownRef}
      preventOverflow
      enterable
      onOpen={() => {
        document
          .getElementsByTagName("body")[0]
          ?.classList?.add("popover-open");

        setFilterOpen(true);
      }}
      onClose={() => {
        document
          .getElementsByTagName("body")[0]
          ?.classList?.remove("popover-open");

        setFilterOpen(false);
      }}
      speaker={
        <Popover
          className={classNames("sidebar-popover p-0", {
            "d-none": recordViewOpen,
          })}
          full
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{ width: "100%", maxWidth: 640 }}
        >
          <FilterDropdownMenu
            currentModule={currentModule}
            query={query}
            filters={filters}
            disableSave={disableSave}
            disableAdvancedFilter={disableAdvancedFilter}
            saveAs={onSave && cPermissions?.cloud_modify_page ? true : false}
            saveNewFilter={saveNewFilter}
            onApply={(out: any, close = true, type: string) => {
              const newQuery = _.clone(query);

              newQuery.Filters = out?.Filters;

              setQuery(newQuery);
              callFunctionIfExists(onApply, newQuery);

              if (newQuery?.Metadata) {
                newQuery.Metadata = { ...newQuery.Metadata, Type: type };
              } else {
                newQuery.Metadata = { Type: type };
              }

              if (close) {
                filterDropdownRef?.current?.close();
              }
            }}
            onModeChange={(mode: string) => {
              setFilterMode(mode);
            }}
            defaultQuery={savedQuery ? savedQuery : null}
            onSaveAsDefault={
              onSave
                ? (out: any) => {
                    const newQuery = _.clone(query);

                    newQuery.Filters = out?.Filters;

                    setQuery(newQuery);
                    callFunctionIfExists(
                      onSave,
                      out?.Filters,
                      out?.Metadata?.Type
                    );

                    filterDropdownRef?.current?.close();
                  }
                : null
            }
            onClear={handleClear}
            onClose={() => {
              filterDropdownRef?.current?.close();
            }}
            checkForFields={checkForFields}
            subitemFieldsOnly={subitemFieldsOnly}
            startingMode={filterMode || "quick"}
            showVariables={showVariables}
            setManageDrawerOpen={setManageDrawerOpen}
            handleUpdateFilters={handleUpdateFilters}
            filterDropdownRef={filterDropdownRef}
          />
        </Popover>
      }
      controlId="newRecordDropdown"
    >
      {customComponent ? (
        <div>
          {customComponent({
            onClick: () => {
              filterOpen
                ? filterDropdownRef?.current?.close()
                : filterDropdownRef?.current?.open();
            },
          })}
        </div>
      ) : (
        <Button
          size="sm"
          className={
            "d-flex align-items-center justify-content-center text-truncate m-0"
          }
          style={{
            maxWidth: 200,
            gap: "10px",
            height: mobileScreen ? 36 : undefined,
            width: mobileScreen ? 32 : undefined,
          }}
          outline={!query?.Filters?.length}
          onClick={() => {
            filterOpen
              ? filterDropdownRef?.current?.close()
              : filterDropdownRef?.current?.open();
          }}
        >
          {!mobileScreen ? (
            <>
              <i
                className={`mdi m-0 p-0 ${
                  query?.Filters?.length ? "mdi-filter" : "mdi-filter-outline"
                }`}
              />
              {handleButtonTitle()}
              <i
                className="mdi mdi-chevron-down m-0 p-0"
                style={{ fontSize: "20px" }}
              />
            </>
          ) : (
            <i className="mdi mdi-filter" style={{ fontSize: 29 }}></i>
          )}
        </Button>
      )}
    </Whisper>
  );
};

export default memo(FiltersButton);
